// BalloonsOverlay.jsx
import React from "react";

const BalloonsOverlay = React.memo(function BalloonsOverlay({
  show = false,
  count = 15,
  zIndex = 6,
  style = {},
  emoji = "🎈",
  // NEW tuning knobs (faster + higher by default)
  minDuration = 3.0,          // was ~7.5s
  maxDuration = 4.2,          // was ~10s
  topBeyondVh = 180,          // rise past the top before fading (was 120)
  startOffsetVh = 16,         // start farther below the frame
  maxDriftVw = 8
}) {
  const balloons = React.useMemo(() => {
    if (!show) return [];
    const durSpread = Math.max(0.1, maxDuration - minDuration);
    return Array.from({ length: count }).map((_, i) => ({
      id: i,
      leftPct: 5 + Math.random() * 90,
      delay: +(Math.random() * 1.4).toFixed(2), // slightly tighter stagger (quicker overall)
      duration: +(minDuration + Math.random() * durSpread).toFixed(2),
      size: 36 + Math.round(Math.random() * 18),
      tilt: (Math.random() * 8 - 4).toFixed(1),
      drift: +(Math.random() * (maxDriftVw * 2) - maxDriftVw).toFixed(1),
    }));
  }, [show, count, minDuration, maxDuration, maxDriftVw]);

  if (!show) return null;

  return (
    <>
      <style>{`
        @keyframes balloonUp {
          0%   { transform: translateY(var(--start)) translateX(0) rotate(var(--tilt)); opacity: 0; }
          10%  { opacity: 1; }
          60%  { transform: translateY(calc(var(--rise) * -0.6)) translateX(calc(var(--drift) / 2)) rotate(calc(var(--tilt) * -1)); }
          100% { transform: translateY(calc(var(--rise) * -1)) translateX(var(--drift)) rotate(var(--tilt)); opacity: 0; }
        }
        .balloon {
          position: absolute;
          will-change: transform, opacity;
          filter: drop-shadow(0 10px 18px rgba(0,0,0,.25));
          user-select: none;
          pointer-events: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .balloon { animation: none !important; opacity: 1 !important; }
        }
      `}</style>

      <div
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
          zIndex,
          ...style,
        }}
      >
        {balloons.map(b => (
          <div
            key={b.id}
            className="balloon"
            style={{
              bottom: "-15%",
              left: `${b.leftPct}%`,
              fontSize: b.size,
              transform: `translateY(${startOffsetVh}vh)`,
              animation: `balloonUp ${b.duration}s ${b.delay}s cubic-bezier(.22,.61,.36,1) forwards`,
              // per-balloon / per-overlay vars
              ["--tilt"]: `${b.tilt}deg`,
              ["--drift"]: `${b.drift}vw`,
              ["--rise"]: `${topBeyondVh}vh`,
              ["--start"]: `${startOffsetVh}vh`,
            }}
          >
            <span role="img" aria-label="balloon">{emoji}</span>
          </div>
        ))}
      </div>
    </>
  );
});

export default BalloonsOverlay;